import React, { useRef, useState, useEffect, memo } from "react";
import { useI18n, usePrevious } from "Hooks";
import _ from "lodash";

// ds
import {
  Button,
  Dropdown,
  DropdownMenu,
  IconButton,
  Tooltip,
  DSProvider,
} from "@toddle-edu/ds-web";
import {
  PlayFilled,
  PauseFilled,
  DownloadOutlined,
} from "@toddle-edu/ds-icons";

// services
import { onAudioFileDownload } from "modules/Services";

// third party
import WaveSurfer from "wavesurfer.js";
import moment from "moment";
import classNames from "classnames";
import { connect } from "react-redux";

// utils
import { isTouchableV3 } from "Utils";
import { allowURLWithCredentials } from "Utils/SecureUrlHelpers";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { notifyAudioPlay, subscribeToAudioPlay } from "./AudioPlaybackRegistry";

// scss
import classes from "./CompactAudioWaveformPlayer.scss";

const AUDIO_SPEED_OPTIONS = [
  {
    key: "dropdown_title",
    label: "Playback speed",
    isItemGroup: true,
    options: [
      {
        key: "0.25",
        label: "0.25x",
      },
      {
        key: "0.5",
        label: "0.5x",
      },
      {
        key: "0.75",
        label: "0.75x",
      },
      {
        key: "1",
        label: "1x",
      },
      {
        key: "1.5",
        label: "1.5x",
      },
      {
        key: "2",
        label: "2x",
      },
    ],
  },
];

const PlayerButton = props => {
  const {
    handleClick,
    playing,
    isUploading,
    testId,
    isTouchableDevice,
    isAppearanceLikeMediaCard = false,
    playerButtonIconStyle = {},
    playerIconClass,
  } = props;

  const onClickIcon = e => {
    e.stopPropagation();
    handleClick();
  };

  const icon = playing ? (
    <PauseFilled size={"xxxx-small"} style={playerButtonIconStyle} />
  ) : (
    <PlayFilled size={"xxxx-small"} style={playerButtonIconStyle} />
  );

  return (
    <ConditionalWrapper
      condition={!isTouchableDevice}
      type="wrapperAsElement"
      wrapper={
        <Tooltip
          tooltip={playing ? "Pause" : "Play"}
          placement="top"
          showArrow={true}
        />
      }
    >
      <IconButton
        icon={icon}
        size={isAppearanceLikeMediaCard ? "medium" : "x-small"}
        variant={"neutral"}
        type={"plain"}
        onClick={onClickIcon}
        disabled={isUploading}
        testId={`${testId}-playPauseButton`}
        className={playerIconClass}
      />
    </ConditionalWrapper>
  );
};

const roundRectIsSupported = () => {
  const canvasElement = document.createElement("canvas");
  const context = canvasElement.getContext("2d");

  if (context && typeof context.roundRect === "function") return true;
  return false;
};

/**
 * Custom wavesurfer hook
 */

const CompactAudioWaveformPlayer = memo(props => {
  const {
    attachment,
    audioLoaded,
    audioPlaybackTime = 0,
    mediaType,
    isUploading,
    onAudioFileDownload,
    pausedRecordingState,
    containerStyle = {},
    testId,
    isAppearanceLikeMediaCard,
    buttonElement = null,
    progressColor = "#525252",
    waveColor = "#CCCCCC",
    cursorColor = "#000000",
    playerButtonIconStyle = {},
    autoPlay = false,
    defaultPlaybackSpeed = "1",
    onAudioSpeedChange,
    getPopupContainer,
    showDownload = true,
    playerIconClass,
    playbackSpeedTriggerClass,
    activePlaybackSpeedTriggerClass,
    playbackSpeedButtonProps = {},
  } = props;

  const [duration, setDuration] = useState("00:00");
  const [playing, setPlaying] = useState(autoPlay);
  const [audioSpeed, setAudioSpeed] = useState(defaultPlaybackSpeed);
  const [isSpeedDropdownOpen, setIsSpeedDropdownOpen] = useState(false);

  const wavesurferRef = useRef(null);
  const waveFormContainerRef = useRef(null);

  const { t } = useI18n();

  const previousAttachment = usePrevious(attachment);

  const MAX_SCREEN_WIDTH = 1200;
  const isTouchableDevice = isTouchableV3(MAX_SCREEN_WIDTH);

  const attachmentUrl = attachment?.url;
  const previousAttachmentUrl = previousAttachment?.url;

  const attachmentId = attachment?.id;
  const previousAttachmentId = previousAttachment?.id;

  useEffect(() => {
    if (!waveFormContainerRef.current) return;
    if (!attachmentUrl) return;

    if (
      wavesurferRef.current &&
      (previousAttachmentId === attachmentId ||
        previousAttachmentUrl === attachmentUrl)
    ) {
      return;
    }

    const wavesurferInstance = WaveSurfer.create({
      barGap: 2,
      barRadius: roundRectIsSupported() ? 2 : 0,
      barWidth: 2,
      cursorColor,
      cursorWidth: 8,
      fetchParams: {
        credentials: allowURLWithCredentials(attachmentUrl)
          ? "include"
          : "same-origin",
      },
      fillParent: true,
      height: 24,
      progressColor,
      url: attachmentUrl,
      waveColor,
      container: waveFormContainerRef.current,
    });

    wavesurferRef.current = wavesurferInstance;
    wavesurferInstance.setPlaybackRate(defaultPlaybackSpeed);

    setPlaying(autoPlay);
    setDuration("00:00");
    setAudioSpeed(defaultPlaybackSpeed);

    // seek to a specific time if prop provided
    wavesurferInstance.seekTo(audioPlaybackTime);
  }, [
    attachmentUrl,
    attachmentId,
    previousAttachmentUrl,
    previousAttachmentId,
    audioPlaybackTime,
    audioLoaded,
    autoPlay,
  ]);

  useEffect(() => {
    if (!wavesurferRef.current) return;

    setAudioSpeed(defaultPlaybackSpeed);
    wavesurferRef.current.setPlaybackRate(defaultPlaybackSpeed);
  }, [defaultPlaybackSpeed]);

  useEffect(() => {
    const wavesurferInstance = wavesurferRef.current;
    if (!wavesurferInstance) return;

    const handleReady = () => {
      if (autoPlay) {
        wavesurferInstance.play();
      }
    };

    if (wavesurferInstance.isReady) {
      if (autoPlay) {
        wavesurferInstance.play();
      }
    } else {
      wavesurferInstance.once("ready", handleReady);
    }

    return () => {
      wavesurferInstance.un("ready", handleReady);
    };
  }, [autoPlay]);

  useEffect(() => {
    const wavesurferInstance = wavesurferRef.current;
    const unsubscribeAudioPlay = subscribeToAudioPlay(
      attachment?.id,
      wavesurferInstance
    );
    const subscriptions = [
      // subscribe to when waveform is mounted and ready event
      wavesurferInstance.on("ready", duration => {
        const totalDurationInSeconds = ~~duration;
        const currentDuration = moment
          .duration(totalDurationInSeconds, "seconds")
          .format("mm:ss", { trim: false });

        wavesurferInstance.setPlaybackRate(audioSpeed);

        if (audioLoaded) audioLoaded();

        setDuration(prevDuration => {
          return currentDuration !== prevDuration && currentDuration !== "00:00"
            ? currentDuration
            : prevDuration;
        });
      }),

      //subscribe to play and pause events
      wavesurferInstance.on("play", () => {
        notifyAudioPlay(attachment?.id);
        setPlaying(true);
      }),
      wavesurferInstance.on("pause", () => {
        setPlaying(false);
      }),

      // subsribe to seek event
      wavesurferInstance.on("seeking", () => {
        // wavesurferInstance.pause();
      }),

      wavesurferInstance.on("finish", () => {
        wavesurferInstance.setTime(0);
      }),
    ];

    return () => {
      unsubscribeAudioPlay();
      subscriptions.forEach(unsubscribe => unsubscribe());
      wavesurferInstance?.destroy();
      wavesurferRef.current = null;
    };
  }, []);

  const changeAudioSpeed = e => {
    const speed = e.key;
    setAudioSpeed(speed);
    wavesurferRef.current?.setPlaybackRate(speed);
    onAudioSpeedChange?.(speed);
    setIsSpeedDropdownOpen(false);
  };

  const containerClasses = classNames({
    [classes.visualContainer]: true,
    [classes.mediaCardTypeContainer]: isAppearanceLikeMediaCard,
  });
  const durationContainerClasses = classNames(
    classes.durationContainer,
    "text-label"
  );
  const playbackSpeedTriggerClasses = classNames({
    [classes.playbackSpeedTrigger]: true,
    [classes.playbackSpeedTriggerMediumSize]: isAppearanceLikeMediaCard,
    [playbackSpeedTriggerClass]: !_.isEmpty(playbackSpeedTriggerClass),
    [activePlaybackSpeedTriggerClass]:
      isSpeedDropdownOpen && !_.isEmpty(activePlaybackSpeedTriggerClass),
  });

  return (
    <DSProvider dsVersion="2.0">
      <div className={containerClasses} style={containerStyle}>
        <div className={classes.buttonContainer}>
          <PlayerButton
            playing={playing}
            handleClick={() => wavesurferRef.current?.playPause()}
            mediaType={mediaType}
            isUploading={isUploading}
            testId={testId}
            isTouchableDevice={isTouchableDevice}
            isAppearanceLikeMediaCard={isAppearanceLikeMediaCard}
            playerButtonIconStyle={playerButtonIconStyle}
            playerIconClass={playerIconClass}
          />
        </div>

        <div
          id="waveform"
          className={classes.waveformContainer}
          ref={waveFormContainerRef}
          onClick={e => {
            e.stopPropagation();
            wavesurferRef.current?.play();
          }}
          onMouseDown={e => {
            e.stopPropagation();
            wavesurferRef.current?.play();
          }}
        />

        <div className={classes.rightContainer}>
          <div
            className={durationContainerClasses}
            onClick={e => e.stopPropagation()}
          >
            {duration}
          </div>

          {!pausedRecordingState && (
            <div
              className={classes.audioSpeedControl}
              // to prevent audio from playing when clicking on the dropdown
              onClick={e => e.stopPropagation()}
            >
              <Dropdown
                getPopupContainer={getPopupContainer}
                overlay={
                  <DropdownMenu
                    showSelection={true}
                    value={audioSpeed}
                    onClick={changeAudioSpeed}
                    options={AUDIO_SPEED_OPTIONS}
                    testId={`${testId}-audioSpeedDropdown`}
                  />
                }
                visible={isSpeedDropdownOpen}
                onVisibleChange={setIsSpeedDropdownOpen}
              >
                <ConditionalWrapper
                  condition={!isTouchableDevice}
                  type="wrapperAsElement"
                  wrapper={
                    <Tooltip
                      tooltip={t("ai:playback_speed")}
                      placement="top"
                      showArrow={true}
                    />
                  }
                >
                  <Button
                    className={playbackSpeedTriggerClasses}
                    variant="neutral"
                    type="outlined"
                    testId={`${testId}-playbackSpeed`}
                    size={isAppearanceLikeMediaCard ? "medium" : "x-small"}
                    {...playbackSpeedButtonProps}
                  >
                    {`${audioSpeed}x`}
                  </Button>
                </ConditionalWrapper>
              </Dropdown>
            </div>
          )}

          {buttonElement}

          {showDownload ? (
            <ConditionalWrapper
              condition={!isTouchableDevice}
              type="wrapperAsElement"
              wrapper={<Tooltip tooltip="Download" placement="top" showArrow />}
            >
              <IconButton
                icon={
                  <DownloadOutlined
                    variant={isAppearanceLikeMediaCard ? "subtle" : "default"}
                  />
                }
                variant={"neutral"}
                type={"plain"}
                onClick={() => onAudioFileDownload(attachment)}
                size={"small"}
                testId={`${testId}-download-audio`}
                isCompact={!isAppearanceLikeMediaCard}
              />
            </ConditionalWrapper>
          ) : null}
        </div>
      </div>
    </DSProvider>
  );
});

CompactAudioWaveformPlayer.defaultProps = {
  mediaType: "primary",
  isUploading: false,
  testId: "compactAudioWaveformPlayer",
  isAppearanceLikeMediaCard: false,
};

CompactAudioWaveformPlayer.displayName = "CompactAudioWaveformPlayer";

const mapActionCreators = { onAudioFileDownload };

export default connect(null, mapActionCreators)(CompactAudioWaveformPlayer);
